import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { RxCross2 } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination, Navigation } from "swiper/modules";

import Topbar from "../../layout/Topbar";
import { propertyConstants } from "../../constants";
import { PROPERTY_IMAGE_TYPES } from "../../system";
import { LocationIcon, AreaOutlineIcon } from "../../assets/icons";
import PropertyTypesIconMapping from "../../assets/icons/PropertyTypesIconMapping";
import { ReactComponent as NextArrowIcon } from "../../assets/icons/slider/next-arrow.svg";
import { ReactComponent as PrevArrowIcon } from "../../assets/icons/slider/prev-arrow.svg";
import CustomSvg from "../../shared/components/custom-svg/CustomSvg";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import { convertCurrency, numberFormatting } from "../../shared/utility";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import { propertyActions } from "../../redux/actions";


const { ALL_FAVOURITES_PROPERTY } = propertyConstants;

const FavouriteProperty = (props) => {
  const {
    userFavouritesProperty,
    getAllFavouritesPropertyForUser,
    allCurrencies,
    defaultCurrency,
    removePropertyFromFavourite,
    spinnerActions,
  } = props;

  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const navigateToPropertyDetail = useCallback((propertyId) => {
    const path = `/favourite-property-detail/${propertyId}`;
    navigate(path);

    // eslint-disable-next-line
  }, []);

  const defaultCurrencyObject = allCurrencies.find(
    (currency) => currency.id === Number(defaultCurrency)
  );

  const getConvertedValue = (property) =>
    convertCurrency(
      property.price,
      property.currency.code,
      defaultCurrencyObject,
      allCurrencies
    );

  useEffect(() => {
    getAllFavouritesPropertyForUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (userFavouritesProperty) {
      setProperties(userFavouritesProperty);
    }

    // eslint-disable-next-line
  }, [userFavouritesProperty]);

  const handleSlideChange = () => {
    setTimeout(() => {
      const activeSlide = document.querySelector(".swiper-slide-active");
      if (activeSlide) {
        const shadowDiv = activeSlide.querySelector(".slide-shadow-div");
        document
          .querySelectorAll(".slide-shadow-div")
          .forEach((el) => el.classList.remove("overlay-div")); // Reset all shadows
        if (shadowDiv) {
          shadowDiv.classList.add("overlay-div");
        }
      }
    }, 0); // Ensure DOM updates before running
  };

  const adjustedSlides =
    properties.length < 6
      ? [
          ...properties,
          ...(properties.length === 3
            ? properties.slice(-3)
            : properties.length === 4
            ? properties.slice(-4)
            : properties.length === 5
            ? properties.slice(-5)
            : properties.length === 1
            ? []
            : [...properties, ...properties]),
        ]
      : properties;

  return (
    <>
      <Topbar />
      <MainContentWrapper>
        {spinner(ALL_FAVOURITES_PROPERTY) > -1 && <MainSpinner />}
        <div className="favourite-container">
          <div className="container">
            {properties.length > 0 &&
              spinner(ALL_FAVOURITES_PROPERTY) === -1 && (
                <Swiper
                  key={properties.length}
                  direction="vertical"
                  effect="coverflow"
                  grabCursor={false}
                  centeredSlides={true}
                  spaceBetween={-70}
                  initialSlide={3}
                  slidesPerView={properties.length < 4 ? 2 : 3}
                  loopAdditionalSlides={1}
                  loop={true}
                  loopFillGroupWithBlank={true}
                  onSlideChange={handleSlideChange}
                  breakpoints={{
                    320: {
                      direction: "vertical",
                      spaceBetween: -70,
                      coverflowEffect: { stretch: 62.5 },
                    },
                    576: {
                      coverflowEffect: { stretch: 62.5 },
                      spaceBetween: -65,
                      direction: "vertical",
                    },
                    768: {
                      coverflowEffect: {
                        stretch: properties.length < 4 ? 250 : 185,
                      },
                      spaceBetween: properties.length < 4 ? -250 : -250,
                      direction: "horizontal",
                    },
                    992: {
                      coverflowEffect: {
                        stretch: properties.length < 4 ? 350 : 250,
                      },
                      spaceBetween: properties.length < 4 ? -360 : -340,
                      direction: "horizontal",
                    },
                    1200: {
                      coverflowEffect: {
                        stretch: properties.length < 4 ? 500 : 370,
                      },
                      spaceBetween: -500,
                      direction: "horizontal",
                    },
                    1400: {
                      coverflowEffect: {
                        stretch: properties.length < 4 ? 500 : 345,
                      },
                      spaceBetween: properties.length < 4 ? -500 : -450,
                      direction: "horizontal",
                    },
                    1600: {
                      coverflowEffect: {
                        stretch: properties.length < 4 ? 750 : 500,
                      },
                      spaceBetween: properties.length < 4 ? -920 : -780,
                      direction: "horizontal",
                    },
                  }}
                  coverflowEffect={{
                    rotate: 0,
                    scale: 0.8,
                    stretch: 65,
                    slideShadows: false,
                  }}
                  pagination={{
                    el: ".swiper-pagination",
                    clickable: true,
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                  }}
                  navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                    clickable: true,
                  }}
                  modules={[EffectCoverflow, Pagination, Navigation]}
                  className="swiper-coverflow-container"
                >
                  {adjustedSlides.map((property) => (
                    <SwiperSlide
                      key={property?.id}
                      onClick={() =>
                        property?.status &&
                        property?.agentSubscriptionStatus &&
                        navigateToPropertyDetail(property.id)
                      }
                    >
                      <div className="position-relative custom-slide">
                        <div className="position-absolute h-100 w-100 slide-shadow-div" />
                        {(!property.status ||
                          !property.agentSubscriptionStatus) && (
                          <div className="inactive-div favourite-inactive d-flex align-items-center justify-content-center flex-column gap-2">
                            <div className="inactive-div-text">
                              This property is no longer available.
                            </div>
                            <RoundedButton
                              className="inactive-div-button py-2 d-flex align-items-center gap-2"
                              onClick={() =>
                                removePropertyFromFavourite(
                                  property.id,
                                  getAllFavouritesPropertyForUser
                                )
                              }
                            >
                              <RxCross2 className="inactive-div-cross-icon" />
                              <div>Remove from Favourites</div>
                            </RoundedButton>
                          </div>
                        )}

                        <img
                          src={
                            property.images?.find(
                              (img) =>
                                img.type === PROPERTY_IMAGE_TYPES.COVER_IMAGE
                            )?.url ?? property.images[0]?.url
                          }
                          alt="slide_image"
                          className="bg-light"
                        />
                        <div className="favourites-info">
                          <div className="d-flex gap-2 justify-content-sm-between align-items-sm-center flex-column flex-sm-row">
                            <div className="property-title line-clamp line-clamp-2">
                              {property.title}
                            </div>
                            <div className="property-price">
                              <div className="d-flex gap-1 justify-content-end">
                                <div>{defaultCurrencyObject?.symbol}</div>
                                <div>
                                  {numberFormatting(
                                    getConvertedValue(property)
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex mt-sm-4 column-gap-3 column-gap-lg-5 px-2 flex-wrap row-gap-2">
                            {property?.propertyType
                              ?.slice(0, 3)
                              ?.map((type) => (
                                <div className="d-flex align-items-center gap-2 gap-lg-3">
                                  <CustomSvg
                                    className="property-icon"
                                    src={PropertyTypesIconMapping[type.icon]}
                                    alt="property type"
                                    color="#fff"
                                  />
                                  <div className="property-icon-text">
                                    {type.title}
                                  </div>
                                </div>
                              ))}
                            <div className="d-flex align-items-center gap-2 gap-lg-3">
                              <div className="property-icon">
                                <img src={AreaOutlineIcon} alt="area" />
                              </div>
                              <div className="property-icon-text">
                                {property?.area}{" "}
                                {property?.areaMeasurementType === 1
                                  ? "m"
                                  : "f"}
                                <sup> 2</sup>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 gap-lg-3">
                              <div className="property-icon">
                                <img src={LocationIcon} alt="location" />
                              </div>
                              <div className="property-icon-text">
                                {property?.location?.city}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                  <div
                    className={`slider-controler ${
                      properties.length > 1 ? "" : "d-none"
                    }`}
                  >
                    <PrevArrowIcon className="swiper-button-prev slider-arrow" />
                    <NextArrowIcon className="swiper-button-next slider-arrow" />
                    <div className="swiper-pagination"></div>
                  </div>
                </Swiper>
              )}
            {properties.length === 0 && (
              <div className="h-100 w-10 favourite-property-not-found">
                <h2>You haven't added any favourite properties yet!</h2>
              </div>
            )}
          </div>
        </div>
      </MainContentWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({
  allCurrencies: state.homeReducer.allCurrencies,
  defaultCurrency: state.homeReducer.defaultCurrency,
  userFavouritesProperty: state.propertyReducer.userFavouritesProperty,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getAllFavouritesPropertyForUser: () =>
    dispatch(propertyActions.getAllFavouritesPropertyForUser()),
  removePropertyFromFavourite: (id, func) =>
    dispatch(propertyActions.removePropertyFromFavourite(id, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FavouriteProperty);
